<template>
  <v-container>
    <v-data-table
      :headers="headers"
      :items="ingresosExtra"
      item-key="id"
      :sort-by="[{ key: 'fecha', order: 'desc' }]"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Ingresos</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="openDialogForNew">
            Nuevo Ingreso
          </v-btn>
        </v-toolbar>
        <v-row class="px-4 pb-2" dense>
          <v-col cols="12" md="3">
            <v-select
              v-model="filtros.categoria_ingreso_id"
              :items="categoriasIngreso"
              item-title="descripcion"
              item-value="id"
              label="Categoría"
              clearable
              density="compact"
              @update:model-value="recargar"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filtros.fecha_inicio"
              label="Desde"
              type="date"
              density="compact"
              @update:model-value="recargar"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filtros.fecha_fin"
              label="Hasta"
              type="date"
              density="compact"
              @update:model-value="recargar"
            />
          </v-col>
        </v-row>
        <v-alert type="info" variant="tonal" density="compact" class="mx-4 mb-2">
          Aquí se registran ingresos extra (comisiones, exámenes, ventas, etc.).
          Los pagos de matrícula tienen su propia gestión.
        </v-alert>
      </template>

      <template v-slot:[`item.fecha`]="{ item }">
        {{ moment(item.fecha).format('DD/MM/YYYY') }}
      </template>

      <template v-slot:[`item.categoria`]="{ item }">
        {{ item.categoria_ingreso?.descripcion }}
      </template>

      <template v-slot:[`item.alumno`]="{ item }">
        <span v-if="item.alumno">
          {{ item.alumno.nombres }} {{ item.alumno.apellido_paterno }}
        </span>
        <span v-else class="text-grey">—</span>
      </template>

      <template v-slot:[`item.monto`]="{ item }">
        <v-chip color="primary" size="small">S/ {{ Number(item.monto).toFixed(2) }}</v-chip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-icon class="me-2" size="small" @click="editItem(item)">mdi-pencil</v-icon>
        <v-icon size="small" color="red" @click="confirmDelete(item)">mdi-delete</v-icon>
      </template>
    </v-data-table>

    <IngresoDialog
      :dialog="dialog"
      :editedItem="editedItem"
      :formTitle="formTitle"
      :categoriasIngreso="categoriasIngreso"
      :alumnos="alumnos"
      @update:dialog="dialog = $event"
      @save="saveDialog"
    />

    <v-dialog v-model="dialogDelete" width="auto" persistent>
      <v-card max-width="400" prepend-icon="mdi-delete" title="Eliminar Ingreso"
        text="¿Está seguro de eliminar este ingreso?">
        <template v-slot:actions>
          <v-spacer />
          <v-btn color="blue-darken-1" text @click="dialogDelete = false">Cancelar</v-btn>
          <v-btn color="red-darken-1" text @click="deleteItemConfirm">Eliminar</v-btn>
        </template>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import IngresoDialog from '@/components/ingreso/IngresoDialog.vue';

export default {
  components: { IngresoDialog },
  data() {
    return {
      moment,
      dialog: false,
      dialogDelete: false,
      headers: [
        { title: 'Fecha', value: 'fecha' },
        { title: 'Categoría', value: 'categoria' },
        { title: 'Descripción', value: 'descripcion' },
        { title: 'Alumno', value: 'alumno' },
        { title: 'Monto', value: 'monto' },
        { title: 'Acciones', value: 'actions', sortable: false },
      ],
      editedIndex: -1,
      editedItem: {},
      alumnos: [],
      filtros: {
        categoria_ingreso_id: null,
        fecha_inicio: '',
        fecha_fin: '',
      },
    };
  },
  computed: {
    ...mapGetters('ingreso', ['ingresosExtra', 'categoriasIngreso']),
    formTitle() {
      return this.editedIndex === -1 ? 'Nuevo Ingreso' : 'Editar Ingreso';
    },
  },
  async created() {
    await this.fetchCategoriasIngreso();
    await this.cargarAlumnos();
    this.recargar();
  },
  methods: {
    ...mapActions('ingreso', [
      'fetchIngresosExtra',
      'getIngresoById',
      'guardarIngreso',
      'updateIngreso',
      'deleteIngreso',
      'fetchCategoriasIngreso',
    ]),
    async cargarAlumnos() {
      try {
        const { data } = await axios.get('/api/alumno');
        this.alumnos = Array.isArray(data) ? data : (data.data || []);
      } catch (e) {
        this.alumnos = [];
      }
    },
    recargar() {
      const params = {};
      if (this.filtros.categoria_ingreso_id) params.categoria_ingreso_id = this.filtros.categoria_ingreso_id;
      if (this.filtros.fecha_inicio) params.fecha_inicio = this.filtros.fecha_inicio;
      if (this.filtros.fecha_fin) params.fecha_fin = this.filtros.fecha_fin;
      this.fetchIngresosExtra(params);
    },
    openDialogForNew() {
      this.editedItem = {};
      this.editedIndex = -1;
      this.dialog = true;
    },
    async editItem(item) {
      const i = await this.getIngresoById(item.id);
      this.editedItem = {
        id: i.id,
        categoria_ingreso_id: i.categoria_ingreso_id,
        alumno_id: i.alumno_id,
        fecha: i.fecha?.slice(0, 10),
        descripcion: i.descripcion,
        monto: Number(i.monto),
        comprobante: i.comprobante || '',
      };
      this.editedIndex = this.ingresosExtra.findIndex(x => x.id === item.id);
      this.dialog = true;
    },
    confirmDelete(item) {
      this.editedItem = { ...item };
      this.dialogDelete = true;
    },
    async deleteItemConfirm() {
      await this.deleteIngreso(this.editedItem.id);
      this.dialogDelete = false;
    },
    async saveDialog(item) {
      const action = this.editedIndex === -1 ? this.guardarIngreso : this.updateIngreso;
      await action(item);
      this.dialog = false;
      this.recargar();
    },
  },
};
</script>
